import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { Bell, Mail, MessageSquare, Clock, Save, RefreshCw } from 'lucide-react';

const InvoiceNotificationSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    email_enabled: true,
    whatsapp_enabled: false,
    channel: 'both',
    cooldown: 300,
    cron_enabled: false,
    cron_day: 25
  });

  // Buscar configurações ao montar componente
  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/system-settings', {
        params: { category: 'notifications' }
      });

      if (response.data && response.data.data) {
        const settingsData = {};
        response.data.data.forEach(setting => {
          if (setting.setting_key === 'invoice_email_enabled') {
            settingsData.email_enabled = setting.setting_value === 'true';
          } else if (setting.setting_key === 'invoice_whatsapp_enabled') {
            settingsData.whatsapp_enabled = setting.setting_value === 'true';
          } else if (setting.setting_key === 'invoice_notification_channel') {
            settingsData.channel = setting.setting_value;
          } else if (setting.setting_key === 'invoice_notification_cooldown') {
            settingsData.cooldown = parseInt(setting.setting_value) || 300;
          } else if (setting.setting_key === 'invoice_cron_enabled') {
            settingsData.cron_enabled = setting.setting_value === 'true';
          } else if (setting.setting_key === 'invoice_cron_day') {
            settingsData.cron_day = parseInt(setting.setting_value) || 25;
          }
        });

        setSettings(prevSettings => ({ ...prevSettings, ...settingsData }));
      }
    } catch (error) {
      console.error('Erro ao carregar configurações:', error);
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Atualizar cada configuração individualmente
      const updates = [
        api.put('/api/system-settings/value/notifications/invoice_email_enabled', {
          value: settings.email_enabled.toString()
        }),
        api.put('/api/system-settings/value/notifications/invoice_whatsapp_enabled', {
          value: settings.whatsapp_enabled.toString()
        }),
        api.put('/api/system-settings/value/notifications/invoice_notification_channel', {
          value: settings.channel
        }),
        api.put('/api/system-settings/value/notifications/invoice_notification_cooldown', {
          value: settings.cooldown.toString()
        }),
        api.put('/api/system-settings/value/notifications/invoice_cron_enabled', {
          value: settings.cron_enabled.toString()
        }),
        api.put('/api/system-settings/value/notifications/invoice_cron_day', {
          value: settings.cron_day.toString()
        })
      ];

      await Promise.all(updates);

      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      toast.error('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
        <span className="ml-2">Carregando configurações...</span>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      <div className="flex items-center mb-6">
        <Bell className="w-6 h-6 text-blue-600 mr-2" />
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
          Notificações de Faturas
        </h2>
      </div>

      <div className="space-y-6">
        {/* Canal Principal */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Canal de Envio Principal
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Apenas Email */}
            <button
              onClick={() => setSettings({ ...settings, channel: 'email' })}
              className={`p-4 border-2 rounded-lg transition-all ${
                settings.channel === 'email'
                  ? 'border-blue-600 bg-blue-50 dark:bg-blue-900/20'
                  : 'border-gray-300 dark:border-gray-600 hover:border-blue-400'
              }`}
            >
              <Mail className={`w-8 h-8 mx-auto mb-2 ${
                settings.channel === 'email' ? 'text-blue-600' : 'text-gray-500'
              }`} />
              <p className="font-medium text-gray-900 dark:text-white">Apenas Email</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Faturas enviadas apenas por email
              </p>
            </button>

            {/* Apenas WhatsApp */}
            <button
              onClick={() => setSettings({ ...settings, channel: 'whatsapp' })}
              className={`p-4 border-2 rounded-lg transition-all ${
                settings.channel === 'whatsapp'
                  ? 'border-green-600 bg-green-50 dark:bg-green-900/20'
                  : 'border-gray-300 dark:border-gray-600 hover:border-green-400'
              }`}
            >
              <MessageSquare className={`w-8 h-8 mx-auto mb-2 ${
                settings.channel === 'whatsapp' ? 'text-green-600' : 'text-gray-500'
              }`} />
              <p className="font-medium text-gray-900 dark:text-white">Apenas WhatsApp</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Faturas enviadas apenas por WhatsApp
              </p>
            </button>

            {/* Ambos */}
            <button
              onClick={() => setSettings({ ...settings, channel: 'both' })}
              className={`p-4 border-2 rounded-lg transition-all ${
                settings.channel === 'both'
                  ? 'border-purple-600 bg-purple-50 dark:bg-purple-900/20'
                  : 'border-gray-300 dark:border-gray-600 hover:border-purple-400'
              }`}
            >
              <div className="flex items-center justify-center mb-2 space-x-1">
                <Mail className={`w-6 h-6 ${
                  settings.channel === 'both' ? 'text-purple-600' : 'text-gray-500'
                }`} />
                <MessageSquare className={`w-6 h-6 ${
                  settings.channel === 'both' ? 'text-purple-600' : 'text-gray-500'
                }`} />
              </div>
              <p className="font-medium text-gray-900 dark:text-white">Email + WhatsApp</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Máxima garantia de entrega
              </p>
            </button>
          </div>
        </div>

        {/* Switches Individuais */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t dark:border-gray-700">
          {/* Email Enabled */}
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div className="flex items-center">
              <Mail className="w-5 h-5 text-blue-600 mr-3" />
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Enviar por Email</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {settings.email_enabled ? 'Ativo' : 'Desativado'}
                </p>
              </div>
            </div>
            <button
              onClick={() => setSettings({ ...settings, email_enabled: !settings.email_enabled })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.email_enabled ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.email_enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* WhatsApp Enabled */}
          <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            <div className="flex items-center">
              <MessageSquare className="w-5 h-5 text-green-600 mr-3" />
              <div>
                <p className="font-medium text-gray-900 dark:text-white">Enviar por WhatsApp</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {settings.whatsapp_enabled ? 'Ativo' : 'Desativado'}
                </p>
              </div>
            </div>
            <button
              onClick={() => setSettings({ ...settings, whatsapp_enabled: !settings.whatsapp_enabled })}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                settings.whatsapp_enabled ? 'bg-green-600' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  settings.whatsapp_enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        </div>

        {/* Cooldown */}
        <div className="pt-4 border-t dark:border-gray-700">
          <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            <Clock className="w-4 h-4 mr-2" />
            Intervalo entre Envios (segundos)
          </label>
          <input
            type="number"
            min="0"
            max="3600"
            step="60"
            value={settings.cooldown}
            onChange={(e) => setSettings({ ...settings, cooldown: parseInt(e.target.value) || 0 })}
            className="w-full md:w-48 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Tempo de espera entre cada envio (padrão: 300s = 5 minutos)
          </p>
        </div>

        {/* Configuração do Cron Job */}
        <div className="pt-4 border-t dark:border-gray-700">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 p-4 rounded-lg mb-4">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
              📅 Envio Automático Mensal
            </h3>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Configure o dia do mês em que as faturas PENDENTES serão enviadas automaticamente aos clientes.
              O sistema executa diariamente às 8h da manhã e envia apenas no dia configurado.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Habilitar Cron */}
            <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
              <div className="flex items-center">
                <RefreshCw className="w-5 h-5 text-purple-600 mr-3" />
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Envio Automático</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {settings.cron_enabled ? 'Habilitado' : 'Desabilitado'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSettings({ ...settings, cron_enabled: !settings.cron_enabled })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  settings.cron_enabled ? 'bg-purple-600' : 'bg-gray-300 dark:bg-gray-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    settings.cron_enabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Dia do Mês */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Dia do Mês para Envio
              </label>
              <input
                type="number"
                min="1"
                max="31"
                step="1"
                value={settings.cron_day}
                onChange={(e) => {
                  const day = parseInt(e.target.value);
                  if (day >= 1 && day <= 31) {
                    setSettings({ ...settings, cron_day: day });
                  }
                }}
                disabled={!settings.cron_enabled}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Dia do mês (1-31) em que as faturas serão enviadas
              </p>
            </div>
          </div>
        </div>

        {/* Aviso */}
        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <p className="text-sm text-yellow-800 dark:text-yellow-300">
            <strong>⚠️ Atenção:</strong> Certifique-se de que o WhatsApp Business está configurado e
            aprovado antes de ativar o envio por WhatsApp. Envios sem aprovação podem resultar em bloqueio.
          </p>
        </div>

        {/* Botões de Ação */}
        <div className="flex items-center justify-end space-x-4 pt-4 border-t dark:border-gray-700">
          <button
            onClick={fetchSettings}
            disabled={loading}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Recarregar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Salvando...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Salvar Configurações
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InvoiceNotificationSettings;
